// client/context/ThemeContext.jsx
// Dark-only: ein fixes Enterprise-Dark-Theme; Persistenz schreibt immer "dark".
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "../constants/api";

const ThemeContext = createContext(null);

export const THEMES = {
  DARK: "dark",
};

const STORAGE_KEY = "wrtk-theme";
const CANONICAL_THEME = THEMES.DARK;

function normalizeStoredTheme(value) {
  if (value === "light" || value === "system") return CANONICAL_THEME;
  return CANONICAL_THEME;
}

/**
 * Theme Provider — WireTrack ist ausschliesslich Dark Mode.
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(CANONICAL_THEME);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const mountedRef = useRef(true);
  const noTransitionTimerRef = useRef(null);

  const resolvedTheme = CANONICAL_THEME;

  useEffect(() => {
    mountedRef.current = true;
    async function loadTheme() {
      document.documentElement.classList.add("no-transitions");

      try {
        const res = await api.get("/profil/theme", { silent: true });
        if (res.data?.ok && res.data?.theme) {
          if (mountedRef.current) {
            setThemeState(normalizeStoredTheme(res.data.theme));
            setIsLoading(false);
          }
          if (noTransitionTimerRef.current) clearTimeout(noTransitionTimerRef.current);
          noTransitionTimerRef.current = setTimeout(() => {
            document.documentElement.classList.remove("no-transitions");
          }, 50);
          return;
        }
      } catch {
        // Nicht eingeloggt
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && (stored === "dark" || stored === "light" || stored === "system")) {
        if (mountedRef.current) setThemeState(normalizeStoredTheme(stored));
      }

      if (mountedRef.current) setIsLoading(false);
      if (noTransitionTimerRef.current) clearTimeout(noTransitionTimerRef.current);
      noTransitionTimerRef.current = setTimeout(() => {
        document.documentElement.classList.remove("no-transitions");
      }, 50);
    }

    loadTheme();
    return () => {
      mountedRef.current = false;
      if (noTransitionTimerRef.current) clearTimeout(noTransitionTimerRef.current);
      document.documentElement.classList.remove("no-transitions");
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("dark-mode");
    root.classList.remove("light-mode");
    root.classList.add("dark");
    root.classList.remove("light");
    root.setAttribute("data-theme", "dark");
    root.style.colorScheme = "dark";

    let metaTheme = document.querySelector('meta[name="theme-color"]');
    if (!metaTheme) {
      metaTheme = document.createElement("meta");
      metaTheme.name = "theme-color";
      document.head.appendChild(metaTheme);
    }
    metaTheme.content = "#05070c";
  }, []);

  const setTheme = useCallback(async (_newTheme) => {
    setThemeState(CANONICAL_THEME);
    localStorage.setItem(STORAGE_KEY, CANONICAL_THEME);
    setIsSaving(true);
    try {
      await api.put("/profil/theme", { theme: CANONICAL_THEME }, { silent: true });
    } catch {
      // LocalStorage ist Fallback
    } finally {
      if (mountedRef.current) setIsSaving(false);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme();
  }, [setTheme]);

  const cycleTheme = useCallback(() => {
    setTheme();
  }, [setTheme]);

  const value = {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    cycleTheme,
    isDark: true,
    isLight: false,
    isSystem: false,
    isLoading,
    isSaving,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

export default ThemeContext;
